import { useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';

import {
  UpdateNarrativeInput,
  useUpdatePublicNarrative,
} from '../../hooks/useUpdatePublicNarrative';
import { validateNarrativeForm } from '../../utils';

import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import { NarrativeForm } from '../../utils/schema';
import { Narrative } from '../../hooks/useNarrativeById';
import { htmlToText } from '@/components/RichTextEditor';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import { NarrativeFormContent } from '../NarrativeFormContent';
import {
  transformFormValuesToValuation,
  transformValuationToFormValues,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { push } from 'redux-first-router';
import { usePastAnnualRevenueEarnings } from '../../hooks/usePastAnnualRevenueEarnings/usePastAnnualRevenueEarnings';

type Props = {
  narrative: Narrative;
};

export const PublicNarrativeEdit = ({ narrative }: Props) => {
  const {
    id: narrativeId,
    title: initialTitle,
    contentBlocks,
    valuation,
    company,
    userHoldsPosition,
    author,
  } = narrative;

  if (!company) throw new Error("Narrative's company is missing");

  const pastAnnualRevenueEarnings = usePastAnnualRevenueEarnings({
    company,
    fiscalData: valuation?.fiscalData,
  });

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    companyId: company.id,
  });

  const { referrer, goBack } = useNarrativeBack();

  const { mutate: updateNarrative, status: updateNarrativeStatus } =
    useUpdatePublicNarrative({
      onSuccess: () => {
        const path = getPath({
          type: ROUTE_PUBLIC_NARRATIVE_EDIT,
          payload: { id: narrativeId },
          query: referrer
            ? {
                company: company.id,
                referrer,
              }
            : {},
        });

        trackEvent({
          action: 'save',
        });

        push(path);
        goBack();
      },
    });

  const initialContent = contentBlocks?.[0]?.contentHtml ?? '';

  const initialContentText = useMemo(
    () => htmlToText(initialContent),
    [initialContent]
  );

  const initialNarrativeForm: NarrativeForm = useMemo(
    () => ({
      ...transformValuationToFormValues(valuation, company),
      userHoldsPosition: userHoldsPosition ?? false,
      title: initialTitle ?? '',
      content: {
        text: initialContentText,
        html: initialContent,
      },
    }),
    [
      company,
      initialContent,
      initialContentText,
      initialTitle,
      userHoldsPosition,
      valuation,
    ]
  );

  const {
    values,
    setValues,
    setPartialValues,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    resetForm,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
  });

  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({ trackEvent, dirty, errors });

  useEffect(() => {
    setValues(initialNarrativeForm);
    trackEvent({
      action: 'landed',
      narrativeId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrativeId]);

  const handleSubmit = () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    const { title, content, userHoldsPosition } = values;
    const { id, companyId } = narrative;

    const payload: UpdateNarrativeInput = {
      id,
      title,
      companyId,
      userHoldsPosition,
      contentBlocks: content.html
        ? [
            {
              contentHtml: content.html,
              // TODO Deprecate title
              title: contentBlocks?.[0]?.title || 'user_defined',
            },
          ]
        : [],
      valuations: [
        transformFormValuesToValuation({
          values,
        }),
      ],
    };
    updateNarrative({ input: payload });
    setSubmitCount(0);
  };

  const isLoading = updateNarrativeStatus === 'pending';

  return (
    <Track>
      <NarrativeFormContent
        errors={errors}
        values={values}
        onChange={setPartialValues}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel="Update"
        company={company}
        initialContentHtml={initialNarrativeForm.content.html}
        author={author}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
      />
    </Track>
  );
};
